import { useEffect, useState } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { UserPlus, Database, Trash2, RotateCcw, Camera, Plus, ClipboardList, Download } from 'lucide-react';

const ROLE_COLORS = {
  admin:   'bg-blue-100 text-blue-700',
  auditor: 'bg-green-100 text-green-700',
  manager: 'bg-purple-100 text-purple-700',
};

export default function Admin() {
  const [users,     setUsers]     = useState([]);
  const [backups,   setBackups]   = useState([]);
  const [batches,   setBatches]   = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [stats,     setStats]     = useState(null);
  const [tab,       setTab]       = useState('users');
  const [showNew,   setShowNew]   = useState(false);
  const [form,      setForm]      = useState({ email: '', full_name: '', role: 'auditor' });
  const [saving,    setSaving]    = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [showNewItem,    setShowNewItem]    = useState(false);
  const [itemForm,       setItemForm]       = useState({ label: '', description: '' });
  const [savingItem,     setSavingItem]     = useState(false);

  function load() {
    api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/admin/backups').then(r => setBackups(r.data)).catch(() => {});
    api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    api.get('/uploads/batches').then(r => setBatches(r.data)).catch(() => {});
    api.get('/uploads/snapshots').then(r => setSnapshots(r.data)).catch(() => {});
    api.get('/checklist-items/', { params: { include_inactive: true } }).then(r => setChecklistItems(r.data)).catch(() => {});
  }

  useEffect(() => { load(); }, []);

  async function createUser() {
    if (!form.email || !form.full_name) {
      toast.error('Email and full name are required'); return;
    }
    setSaving(true);
    try {
      await api.post('/admin/users', form);
      toast.success(`User ${form.email} created`);
      setForm({ email: '', full_name: '', role: 'auditor' });
      setShowNew(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  }

  async function toggleUser(user) {
    try {
      await api.put(`/admin/users/${user.id}`, { active: user.active ? 0 : 1 });
      toast.success(user.active ? 'User deactivated' : 'User activated');
      load();
    } catch { toast.error('Update failed'); }
  }

  async function changeRole(user, role) {
    try {
      await api.put(`/admin/users/${user.id}`, { role });
      toast.success(`Role updated to ${role}`);
      load();
    } catch { toast.error('Update failed'); }
  }

  async function createChecklistItem() {
    if (!itemForm.label.trim()) { toast.error('Label is required'); return; }
    setSavingItem(true);
    try {
      await api.post('/checklist-items/', itemForm);
      toast.success(`Checklist item "${itemForm.label}" added`);
      setItemForm({ label: '', description: '' });
      setShowNewItem(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setSavingItem(false); }
  }

  async function toggleChecklistItem(item) {
    try {
      await api.patch(`/checklist-items/${item.id}`, { active: !item.active });
      toast.success(item.active ? 'Item deactivated' : 'Item activated');
      load();
    } catch { toast.error('Update failed'); }
  }

  async function deleteChecklistItem(item) {
    if (!window.confirm(`Permanently delete grading item "${item.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/checklist-items/${item.id}`);
      toast.success('Checklist item deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  }

  async function downloadBackup() {
    try {
      toast.loading('Preparing backup…', { id: 'backup' });
      const r = await api.get('/admin/backup/export', { responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `pcc_backup_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.json`;
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded', { id: 'backup' });
      load();
    } catch { toast.error('Backup failed', { id: 'backup' }); }
  }

  async function deleteBatch(batch) {
    if (!window.confirm(`Delete batch "${batch.filename}" and all ${batch.job_count} jobs in it?`)) return;
    try {
      await api.delete(`/uploads/batches/${batch.id}`);
      toast.success('Batch deleted');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  }

  async function createSnapshot() {
    try {
      const r = await api.post('/uploads/snapshots');
      toast.success(`Snapshot created`);
      load();
    } catch { toast.error('Snapshot failed'); }
  }

  async function restoreSnapshot(snap) {
    if (!window.confirm(`Restore to "${snap.name}"?\n\nThis will DELETE all data added after this snapshot. This cannot be undone.`)) return;
    try {
      const r = await api.post(`/uploads/restore/${snap.id}`);
      toast.success(r.data.message);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Restore failed'); }
  }

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
        <p className="text-sm text-gray-500 mt-1">User management, backups, and system settings</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            ['Total Jobs',   stats.total_jobs],
            ['Total Audits', stats.total_audits],
            ['Active Users', stats.total_users],
            ['Imports',      stats.total_batches],
            ['Backups',      stats.backup_count],
          ].map(([label, value]) => (
            <div key={label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
              <div className="text-2xl font-bold text-gray-800">{value}</div>
              <div className="text-xs text-gray-400 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[['users','Users'],['checklist','Grading Items'],['data','Data & Snapshots'],['backups','Backups']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${tab === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Users tab */}
      {tab === 'users' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            <strong>Note:</strong> Users are created automatically when they sign in with Microsoft SSO.
            Use this form to pre-create a user or set a role before they first log in.
          </div>

          <div className="flex justify-end">
            <button onClick={() => setShowNew(v => !v)}
              className="flex items-center gap-2 px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A]">
              <UserPlus size={16} /> Add User
            </button>
          </div>

          {showNew && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Add User</h3>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ['Work Email *', 'email',     'email'],
                  ['Full Name *',  'full_name', 'text'],
                ].map(([label, key, type]) => (
                  <div key={key}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                    <input type={type} value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder={key === 'email' ? 'user@delair.com' : ''}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                  <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]">
                    <option value="auditor">Auditor</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={createUser} disabled={saving}
                  className="px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A] disabled:opacity-40">
                  {saving ? 'Adding...' : 'Add User'}
                </button>
                <button onClick={() => setShowNew(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-500 text-sm rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#3D2B1A] text-white">
                  {['Full Name','Email','Role','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr key={u.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">{u.full_name}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <select value={u.role} onChange={e => changeRole(u, e.target.value)}
                        className="text-xs px-2 py-1 border border-gray-200 rounded-lg focus:outline-none">
                        <option value="auditor">Auditor</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${u.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {u.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleUser(u)}
                        className="text-xs px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
                        {u.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grading Items tab */}
      {tab === 'checklist' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700 flex items-start gap-2">
            <ClipboardList size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>Custom grading items</strong> appear at the bottom of every audit's checklist (marked "Custom"),
              and count toward each audit's pass/fail score and result alongside the standard items.
              Deactivating an item hides it from new audits without deleting past answers.
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => setShowNewItem(v => !v)}
              className="flex items-center gap-2 px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A]">
              <Plus size={16} /> Add Grading Item
            </button>
          </div>

          {showNewItem && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-700">Add Grading Item</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Label *</label>
                  <input type="text" value={itemForm.label}
                    onChange={e => setItemForm(f => ({ ...f, label: e.target.value }))}
                    placeholder="e.g. Drain Line Photo"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                  <input type="text" value={itemForm.description}
                    onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Shown to auditors as guidance"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E8761A]"
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={createChecklistItem} disabled={savingItem}
                  className="px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A] disabled:opacity-40">
                  {savingItem ? 'Adding...' : 'Add Item'}
                </button>
                <button onClick={() => setShowNewItem(false)}
                  className="px-4 py-2 border border-gray-200 text-gray-500 text-sm rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#3D2B1A] text-white">
                  {['Label','Description','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {checklistItems.length === 0
                  ? <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No custom grading items yet — add one above</td></tr>
                  : checklistItems.map((item, i) => (
                    <tr key={item.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 font-medium text-gray-800">{item.label}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs max-w-[320px] truncate">{item.description || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {item.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button onClick={() => toggleChecklistItem(item)}
                            className="text-xs px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
                            {item.active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button onClick={() => deleteChecklistItem(item)} title="Delete item"
                            className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Data & Snapshots tab */}
      {tab === 'data' && (
        <div className="space-y-8">
          {/* Snapshots */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Snapshots</h2>
                <p className="text-xs text-gray-400">Restore points created automatically before each upload, or manually.</p>
              </div>
              <button onClick={createSnapshot}
                className="flex items-center gap-2 px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A]">
                <Camera size={16} /> Create Snapshot Now
              </button>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#3D2B1A] text-white">
                    {['Name','Created By','Date','Jobs','Audits','Action'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {snapshots.length === 0
                    ? <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No snapshots yet — they're created automatically before each upload</td></tr>
                    : snapshots.map((s, i) => (
                      <tr key={s.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                        <td className="px-4 py-3 text-gray-700 font-medium">{s.name}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{s.created_by}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{s.jobs_count}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{s.audits_count}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => restoreSnapshot(s)}
                            className="flex items-center gap-1 text-xs px-3 py-1 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50">
                            <RotateCcw size={11} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Upload batches */}
          <div className="space-y-3">
            <div>
              <h2 className="text-base font-semibold text-gray-800">Upload Batches</h2>
              <p className="text-xs text-gray-400">Delete a batch to remove all jobs imported in that upload.</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#3D2B1A] text-white">
                    {['Filename','Type','Jobs','Imported By','Date','Action'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {batches.length === 0
                    ? <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No uploads yet</td></tr>
                    : batches.map((b, i) => (
                      <tr key={b.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{b.filename}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.batch_type === 'audit-results' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                            {b.batch_type === 'audit-results' ? 'Audit Results' : 'Jobs'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-600">{b.job_count}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{b.imported_by_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => deleteBatch(b)}
                            className="flex items-center gap-1 text-xs px-3 py-1 border border-red-200 text-red-600 rounded-lg hover:bg-red-50">
                            <Trash2 size={11} /> Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Backups tab */}
      {tab === 'backups' && (
        <div className="space-y-6">
          {/* Real data export */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-800">Download Full Backup</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Exports all jobs, audits, audit history, checklist items, and users as a JSON file you can save locally.
                </p>
              </div>
              <button onClick={downloadBackup}
                className="flex items-center gap-2 px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A] whitespace-nowrap">
                <Download size={16} /> Download Backup
              </button>
            </div>
          </div>

          {/* Backup history */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">Backup History</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#3D2B1A] text-white">
                  {['Filename','Created By','Date','Size'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {backups.length === 0
                  ? <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No backups yet — click Download Backup to create the first one</td></tr>
                  : backups.map((b, i) => (
                    <tr key={b.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 text-gray-700 font-mono text-xs">{b.filename}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{b.created_by}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{new Date(b.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {b.size_bytes > 0 ? `${(b.size_bytes / 1024).toFixed(1)} KB` : '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {/* Snapshots (restore points) */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            <strong>Restore points:</strong> Snapshots capture the exact state of your data at a point in time.
            Restoring deletes everything added <em>after</em> that snapshot — jobs, audits, and imports.
            Snapshots are created automatically before every upload and every 4 hours while the app is running.
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={createSnapshot}
              className="flex items-center gap-2 px-4 py-2 bg-[#3D2B1A] text-white text-sm font-medium rounded-lg hover:bg-[#E8761A]">
              <Camera size={16} /> Create Snapshot Now
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">Snapshots — click Restore to roll back to that point</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#3D2B1A] text-white">
                  {['Name','Created By','Date','Jobs','Audits','Action'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {snapshots.length === 0
                  ? <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">
                      No snapshots yet — upload some data to create the first snapshot automatically
                    </td></tr>
                  : snapshots.map((s, i) => (
                    <tr key={s.id} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 text-gray-700 font-medium text-xs">{s.name}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{s.created_by}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">{s.jobs_count}</td>
                      <td className="px-4 py-3 text-center text-gray-600 text-xs">{s.audits_count}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => restoreSnapshot(s)}
                          className="flex items-center gap-1 text-xs px-3 py-1 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 font-medium">
                          <RotateCcw size={11} /> Restore
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
